import React, { useState, useEffect, useCallback } from 'react'
import { Header } from '../components/layout/Header'

interface Endpoint {
  id: string
  category: string
  method: string
  path: string
  summary: string
  description: string
  parameters: Array<{
    name: string
    in: string
    type: string
    description?: string
    required?: boolean
  }>
  body?: {
    type: string
    properties: Record<string, { type: string; example?: string }>
  }
  responses: Record<string, { description: string; example?: any }>
  tags: string[]
  auth_required?: boolean
}

interface Category {
  name: string
  count: number
  endpoints: string[]
}

const API_BASE = '/api/api-playground'

const METHOD_COLORS: Record<string, { bg: string; text: string }> = {
  GET: { bg: '#D1FAE5', text: '#059669' },
  POST: { bg: '#DBEAFE', text: '#2563EB' },
  PUT: { bg: '#FEF3C7', text: '#D97706' },
  PATCH: { bg: '#E0E7FF', text: '#4F46E5' },
  DELETE: { bg: '#FEE2E2', text: '#DC2626' }
}

const APIPlaygroundPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browse' | 'test' | 'history'>('browse')
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)

  // Code generation
  const [selectedLanguage, setSelectedLanguage] = useState('curl')
  const [generatedCode, setGeneratedCode] = useState('')

  // Test request
  const [testParams, setTestParams] = useState<Record<string, string>>({})
  const [testBody, setTestBody] = useState('')
  const [testResponse, setTestResponse] = useState<any>(null)
  const [testLoading, setTestLoading] = useState(false)
  const [responseTime, setResponseTime] = useState<number | null>(null)

  // History
  const [requestHistory, setRequestHistory] = useState<any[]>([])

  const fetchEndpoints = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedCategory) params.set('category', selectedCategory)
      if (searchQuery) params.set('search', searchQuery)

      const res = await fetch(`${API_BASE}/endpoints?${params}`)
      if (res.ok) {
        setEndpoints(await res.json())
      }
    } catch (err) {
      console.error('Error fetching endpoints:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedCategory, searchQuery])

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/categories`)
      if (res.ok) {
        setCategories(await res.json())
      }
    } catch (err) {
      console.error('Error fetching categories:', err)
    }
  }

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/requests/history?limit=50`)
      if (res.ok) {
        setRequestHistory(await res.json())
      }
    } catch (err) {
      console.error('Error fetching history:', err)
    }
  }

  const generateCode = async (endpointId: string, language: string) => {
    try {
      const res = await fetch(`${API_BASE}/code/${endpointId}?language=${language}`)
      if (res.ok) {
        const data = await res.json()
        setGeneratedCode(data.code)
      }
    } catch (err) {
      console.error('Error generating code:', err)
    }
  }

  useEffect(() => {
    fetchCategories()
    fetchEndpoints()
  }, [fetchEndpoints])

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory()
    }
  }, [activeTab])

  useEffect(() => {
    if (selectedEndpoint) {
      generateCode(selectedEndpoint.id, selectedLanguage)

      // Initialize test params
      const params: Record<string, string> = {}
      selectedEndpoint.parameters.forEach(p => {
        params[p.name] = ''
      })
      setTestParams(params)

      // Initialize test body
      if (selectedEndpoint.body?.properties) {
        const body: Record<string, any> = {}
        Object.entries(selectedEndpoint.body.properties).forEach(([key, config]) => {
          body[key] = config.example || ''
        })
        setTestBody(JSON.stringify(body, null, 2))
      } else {
        setTestBody('')
      }

      setTestResponse(null)
    }
  }, [selectedEndpoint, selectedLanguage])

  const executeTest = async () => {
    if (!selectedEndpoint) return

    setTestLoading(true)
    setTestResponse(null)

    let path = selectedEndpoint.path
    const queryParams = new URLSearchParams()

    // Replace path parameters and build query params
    selectedEndpoint.parameters.forEach(p => {
      const value = testParams[p.name]
      if (p.in === 'path') {
        path = path.replace(`{${p.name}}`, value || p.name)
      } else if (p.in === 'query' && value) {
        queryParams.set(p.name, value)
      }
    })

    const url = queryParams.toString() ? `${path}?${queryParams}` : path

    try {
      const startTime = Date.now()

      const options: RequestInit = {
        method: selectedEndpoint.method,
        headers: { 'Content-Type': 'application/json' }
      }

      if (['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method) && testBody) {
        options.body = testBody
      }

      const res = await fetch(url, options)
      const endTime = Date.now()
      setResponseTime(endTime - startTime)

      let data
      const contentType = res.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        data = await res.json()
      } else {
        data = await res.text()
      }

      setTestResponse({
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        data
      })

      // Log request
      await fetch(`${API_BASE}/requests/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint_id: selectedEndpoint.id,
          method: selectedEndpoint.method,
          path: url,
          status_code: res.status,
          response_time_ms: endTime - startTime
        })
      })
    } catch (err: any) {
      setTestResponse({
        status: 0,
        statusText: 'Error',
        error: err.message
      })
    } finally {
      setTestLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const styles: Record<string, React.CSSProperties> = {
    container: { minHeight: '100vh', backgroundColor: '#f5f7fa' },
    content: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },
    title: { fontSize: '28px', fontWeight: 700, color: '#1F2937', marginBottom: '8px' },
    subtitle: { color: '#6B7280', marginBottom: '24px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '16px' },
    tab: { padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3F4F6', color: '#6B7280' },
    tabActive: { backgroundColor: '#4A90E2', color: 'white' },
    layout: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' },
    sidebar: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content' },
    main: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    searchInput: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', marginBottom: '16px' },
    categoryItem: { padding: '10px 12px', borderRadius: '6px', cursor: 'pointer', marginBottom: '4px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    categoryActive: { backgroundColor: '#EFF6FF', color: '#2563EB' },
    endpointList: { maxHeight: '400px', overflowY: 'auto' as const },
    endpointItem: { padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', marginBottom: '8px', cursor: 'pointer', transition: 'border-color 0.2s' },
    endpointItemActive: { borderColor: '#4A90E2', backgroundColor: '#F0F7FF' },
    methodBadge: { display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 },
    endpointPath: { fontFamily: 'monospace', fontSize: '13px', color: '#374151', marginTop: '6px' },
    endpointSummary: { fontSize: '14px', fontWeight: 500, color: '#1F2937' },
    detailsSection: { marginBottom: '24px' },
    sectionTitle: { fontSize: '16px', fontWeight: 600, color: '#1F2937', marginBottom: '12px' },
    paramTable: { width: '100%', borderCollapse: 'collapse' as const },
    paramTh: { textAlign: 'left' as const, padding: '8px', borderBottom: '2px solid #E5E7EB', fontSize: '13px', color: '#6B7280' },
    paramTd: { padding: '8px', borderBottom: '1px solid #F3F4F6', fontSize: '14px' },
    codeBlock: { backgroundColor: '#1F2937', color: '#E5E7EB', padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', overflowX: 'auto' as const, whiteSpace: 'pre-wrap' as const },
    languageSelect: { padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '13px', marginBottom: '12px' },
    testInput: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'monospace' },
    testButton: { padding: '10px 24px', backgroundColor: '#4A90E2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' },
    responseBox: { backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '16px', marginTop: '16px' },
    statusBadge: { display: 'inline-block', padding: '4px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: 500 },
    copyButton: { padding: '6px 12px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
    historyItem: { padding: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '8px' }
  }

  const renderEndpointDetails = () => {
    if (!selectedEndpoint) {
      return (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>Select an endpoint</p>
          <p>Choose an endpoint from the list to view details and test it</p>
        </div>
      )
    }

    const methodStyle = METHOD_COLORS[selectedEndpoint.method] || METHOD_COLORS.GET

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ ...styles.methodBadge, backgroundColor: methodStyle.bg, color: methodStyle.text }}>
                {selectedEndpoint.method}
              </span>
              <code style={{ fontSize: '16px', color: '#374151' }}>{selectedEndpoint.path}</code>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>{selectedEndpoint.summary}</h2>
            <p style={{ color: '#6B7280' }}>{selectedEndpoint.description}</p>
          </div>
          {selectedEndpoint.auth_required && (
            <span style={{ ...styles.statusBadge, backgroundColor: '#FEF3C7', color: '#D97706' }}>
              Auth Required
            </span>
          )}
        </div>

        {/* Parameters */}
        {selectedEndpoint.parameters.length > 0 && (
          <div style={styles.detailsSection}>
            <h3 style={styles.sectionTitle}>Parameters</h3>
            <table style={styles.paramTable}>
              <thead>
                <tr>
                  <th style={styles.paramTh}>Name</th>
                  <th style={styles.paramTh}>In</th>
                  <th style={styles.paramTh}>Type</th>
                  <th style={styles.paramTh}>Required</th>
                  <th style={styles.paramTh}>Description</th>
                </tr>
              </thead>
              <tbody>
                {selectedEndpoint.parameters.map((param, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.paramTd, fontFamily: 'monospace' }}>{param.name}</td>
                    <td style={styles.paramTd}>{param.in}</td>
                    <td style={styles.paramTd}>{param.type}</td>
                    <td style={styles.paramTd}>{param.required ? 'Yes' : 'No'}</td>
                    <td style={styles.paramTd}>{param.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Request Body */}
        {selectedEndpoint.body && (
          <div style={styles.detailsSection}>
            <h3 style={styles.sectionTitle}>Request Body</h3>
            <pre style={{ ...styles.codeBlock, backgroundColor: '#F3F4F6', color: '#374151' }}>
              {JSON.stringify(selectedEndpoint.body.properties, null, 2)}
            </pre>
          </div>
        )}

        {/* Code Generation */}
        <div style={styles.detailsSection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Code Snippet</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                value={selectedLanguage}
                onChange={e => setSelectedLanguage(e.target.value)}
                style={styles.languageSelect}
              >
                <option value="curl">cURL</option>
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
              </select>
              <button
                onClick={() => copyToClipboard(generatedCode)}
                style={styles.copyButton}
              >
                Copy
              </button>
            </div>
          </div>
          <pre style={styles.codeBlock}>{generatedCode}</pre>
        </div>

        {/* Test Section */}
        <div style={styles.detailsSection}>
          <h3 style={styles.sectionTitle}>Try It Out</h3>

          {selectedEndpoint.parameters.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {selectedEndpoint.parameters.map((param, idx) => (
                <div key={idx} style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                    {param.name} {param.required && <span style={{ color: '#DC2626' }}>*</span>}
                  </label>
                  <input
                    type="text"
                    value={testParams[param.name] || ''}
                    onChange={e => setTestParams({ ...testParams, [param.name]: e.target.value })}
                    placeholder={param.description || param.name}
                    style={styles.testInput}
                  />
                </div>
              ))}
            </div>
          )}

          {selectedEndpoint.body && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                Request Body
              </label>
              <textarea
                value={testBody}
                onChange={e => setTestBody(e.target.value)}
                style={{ ...styles.testInput, minHeight: '120px', resize: 'vertical' }}
              />
            </div>
          )}

          <button
            onClick={executeTest}
            disabled={testLoading}
            style={{ ...styles.testButton, opacity: testLoading ? 0.6 : 1 }}
          >
            {testLoading ? 'Sending...' : 'Send Request'}
          </button>

          {testResponse && (
            <div style={styles.responseBox}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: testResponse.status >= 200 && testResponse.status < 300 ? '#D1FAE5' : '#FEE2E2',
                  color: testResponse.status >= 200 && testResponse.status < 300 ? '#059669' : '#DC2626'
                }}>
                  {testResponse.status} {testResponse.statusText}
                </span>
                {responseTime && (
                  <span style={{ fontSize: '13px', color: '#6B7280' }}>{responseTime}ms</span>
                )}
              </div>
              <pre style={{ ...styles.codeBlock, maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify(testResponse.data || testResponse.error, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>API Playground</h1>
        <p style={styles.subtitle}>Explore, test, and generate code for API endpoints</p>

        <div style={styles.tabs}>
          {(['browse', 'test', 'history'] as const).map(tab => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {(activeTab === 'browse' || activeTab === 'test') && (
          <div style={styles.layout}>
            <div style={styles.sidebar}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search endpoints..."
                style={styles.searchInput}
              />

              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    ...styles.categoryItem,
                    ...(!selectedCategory ? styles.categoryActive : {})
                  }}
                  onClick={() => setSelectedCategory('')}
                >
                  <span>All Endpoints</span>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    {endpoints.length}
                  </span>
                </div>
                {categories.map(cat => (
                  <div
                    key={cat.name}
                    style={{
                      ...styles.categoryItem,
                      ...(selectedCategory === cat.name ? styles.categoryActive : {})
                    }}
                    onClick={() => setSelectedCategory(cat.name)}
                  >
                    <span>{cat.name}</span>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{cat.count}</span>
                  </div>
                ))}
              </div>

              <div style={styles.endpointList}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>Loading...</div>
                ) : (
                  endpoints.map(endpoint => {
                    const methodStyle = METHOD_COLORS[endpoint.method] || METHOD_COLORS.GET
                    return (
                      <div
                        key={endpoint.id}
                        style={{
                          ...styles.endpointItem,
                          ...(selectedEndpoint?.id === endpoint.id ? styles.endpointItemActive : {})
                        }}
                        onClick={() => setSelectedEndpoint(endpoint)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            ...styles.methodBadge,
                            backgroundColor: methodStyle.bg,
                            color: methodStyle.text
                          }}>
                            {endpoint.method}
                          </span>
                          <span style={styles.endpointSummary}>{endpoint.summary}</span>
                        </div>
                        <div style={styles.endpointPath}>{endpoint.path}</div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            <div style={styles.main}>
              {renderEndpointDetails()}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div style={styles.main}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>Request History</h2>
            {requestHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                No request history yet. Start testing endpoints to see history here.
              </div>
            ) : (
              requestHistory.map((req, idx) => {
                const methodStyle = METHOD_COLORS[req.method] || METHOD_COLORS.GET
                return (
                  <div key={idx} style={styles.historyItem}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          ...styles.methodBadge,
                          backgroundColor: methodStyle.bg,
                          color: methodStyle.text
                        }}>
                          {req.method}
                        </span>
                        <code style={{ fontSize: '14px' }}>{req.path}</code>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: req.status_code >= 200 && req.status_code < 300 ? '#D1FAE5' : '#FEE2E2',
                          color: req.status_code >= 200 && req.status_code < 300 ? '#059669' : '#DC2626'
                        }}>
                          {req.status_code}
                        </span>
                        <span style={{ fontSize: '13px', color: '#6B7280' }}>{req.response_time_ms}ms</span>
                        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                          {new Date(req.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default APIPlaygroundPage
